import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import {
  formatAuditDescription,
  formatAuditTimestamp,
  formatAuditUser,
  formatHttpMethod,
  formatRequestPath,
} from './formatters';
import { parseAuditDetails, stringifyAuditDetailValue, toDetailObjectEntries } from './detailsParser';

const BRAND = {
  headerBg: '5e894f',
  headerFont: 'FFFFFF',
  evenRow: 'F7FAF6',
  oddRow: 'FFFFFF',
};

const safeText = (value, fallback = '—') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const EXCEL_MAX_CELL_LENGTH = 32767;

const sanitizeExcelXmlText = (input) => {
  if (!input) return '';

  let output = '';
  for (const char of input) {
    const codePoint = char.codePointAt(0);
    const isAllowedXmlChar =
      codePoint === 0x9 ||
      codePoint === 0xA ||
      codePoint === 0xD ||
      (codePoint >= 0x20 && codePoint <= 0xD7FF) ||
      (codePoint >= 0xE000 && codePoint <= 0xFFFD) ||
      (codePoint >= 0x10000 && codePoint <= 0x10FFFF);
    const isNonCharacter = (codePoint & 0xFFFF) === 0xFFFE || (codePoint & 0xFFFF) === 0xFFFF;
    output += isAllowedXmlChar && !isNonCharacter ? char : ' ';
  }

  return output;
};

const toExcelSafeValue = (value, fallback = '—') => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'number') return value;

  return sanitizeExcelXmlText(String(value));
};

const chunkExcelText = (text) => {
  const normalized = toExcelSafeValue(text, '');
  if (!normalized) return [''];
  if (normalized.length <= EXCEL_MAX_CELL_LENGTH) return [normalized];

  const chunks = [];
  for (let index = 0; index < normalized.length; index += EXCEL_MAX_CELL_LENGTH) {
    chunks.push(normalized.slice(index, index + EXCEL_MAX_CELL_LENGTH));
  }
  return chunks;
};

const buildTimestamp = () => {
  const now = new Date();
  const parts = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
    String(now.getSeconds()).padStart(2, '0'),
  ];
  return parts.join('');
};

const buildFiltersText = (filters = {}) => {
  const entries = [];
  if (filters.moduleKey) entries.push(`Módulo: ${filters.moduleKey}`);
  if (filters.userLabel || filters.userUuid) entries.push(`Usuario: ${filters.userLabel || filters.userUuid}`);
  if (filters.httpMethod) entries.push(`Método: ${filters.httpMethod}`);
  if (filters.endpoint) entries.push(`Endpoint: ${filters.endpoint}`);
  if (filters.search) entries.push(`Búsqueda: ${filters.search}`);
  if (filters.startDate) entries.push(`Desde: ${filters.startDate}`);
  if (filters.endDate) entries.push(`Hasta: ${filters.endDate}`);
  return entries.length ? entries.join(' | ') : 'Sin filtros específicos';
};

const mapActionSummary = (action) => ({
  id: action?.id ?? '—',
  fecha: formatAuditTimestamp(action?.created_at),
  usuario: formatAuditUser(action),
  modulo: action?.module?.display_name || action?.module?.key || '—',
  accion: formatAuditDescription(action),
  metodo: formatHttpMethod(action?.http_method),
  endpoint: formatRequestPath(action?.request_path, action?.endpoint),
  estado: action?.status_code ?? '—',
  ip: action?.remote_addr || '—',
});

const mapActionDetailLines = (action, resolveReference) => {
  const { changes, changeSummary, entityReference, additionalDetails } = parseAuditDetails(action, {
    resolveReference,
  });

  const changesText = changes.length
    ? changes.map((change) => `${change.label}: ${change.before} -> ${change.after}`).join('\n')
    : changeSummary?.message || 'Sin cambios detectados';

  const additionalText = additionalDetails.length
    ? additionalDetails
        .map((detail) => {
          if (detail?.value && typeof detail.value === 'object' && !Array.isArray(detail.value)) {
            const objectEntries = toDetailObjectEntries(detail.value, { resolveReference });
            if (objectEntries.length) {
              const normalized = objectEntries
                .map((entry) => `${entry.label}: ${safeText(entry.value, 'Sin dato')}`)
                .join(' | ');
              return `${detail.label}: ${normalized}`;
            }
          }
          return `${detail.label}: ${stringifyAuditDetailValue(detail.value)}`;
        })
        .join('\n')
    : 'Sin detalles adicionales';

  return {
    entity: entityReference?.display || '—',
    changesCount: typeof changeSummary?.count === 'number' ? changeSummary.count : changes.length,
    changesText,
    additionalText,
  };
};

const styleHeader = (row) => {
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BRAND.headerBg },
    };
    cell.font = {
      color: { argb: BRAND.headerFont },
      bold: true,
      size: 11,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });
};

const styleDataRow = (row, index) => {
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: index % 2 === 0 ? BRAND.evenRow : BRAND.oddRow },
    };
    cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });
};

const writePdfTitle = (doc, text) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(45, 45, 45);
  doc.text(text, 14, 16);
};

const writePdfMeta = (doc, { filtersText, total }) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`Generado: ${new Date().toLocaleString('es-AR')}`, 14, 24);
  doc.text(`Registros: ${total}`, 14, 29);
  const wrappedFilters = doc.splitTextToSize(`Filtros: ${filtersText}`, 180);
  doc.text(wrappedFilters, 14, 34);
  const finalY = 34 + wrappedFilters.length * 5;
  doc.setDrawColor(180, 180, 180);
  doc.line(14, finalY + 2, 196, finalY + 2);
  return finalY + 8;
};

const ensurePdfSpace = (doc, y, extra = 20) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + extra <= pageHeight - 12) return y;
  doc.addPage();
  return 16;
};

export const exportAuditActionsToExcel = async ({
  actions = [],
  filters = {},
  resolveReference,
  filenamePrefix = 'Auditoria',
}) => {
  const workbook = new ExcelJS.Workbook();
  const auditSheet = workbook.addWorksheet('Auditoria');
  const columns = [
    { header: 'ID', key: 'id', width: 8 },
    { header: 'Fecha/Hora', key: 'fecha', width: 20 },
    { header: 'Usuario', key: 'usuario', width: 24 },
    { header: 'Módulo', key: 'modulo', width: 22 },
    { header: 'Acción', key: 'accion', width: 48 },
    { header: 'Método', key: 'metodo', width: 10 },
    { header: 'Endpoint', key: 'endpoint', width: 42 },
    { header: 'Estado', key: 'estado', width: 10 },
    { header: 'IP', key: 'ip', width: 18 },
    { header: 'Registro afectado', key: 'entity', width: 30 },
    { header: 'Cantidad de cambios', key: 'changesCount', width: 16 },
    { header: 'Cambios detectados', key: 'changesText', width: 80 },
    { header: 'Detalles adicionales legibles', key: 'additionalText', width: 90 },
  ];

  columns.forEach((column, index) => {
    auditSheet.getColumn(index + 1).width = column.width;
  });

  auditSheet.insertRow(1, ['Auditoría de acciones']);
  auditSheet.mergeCells(1, 1, 1, columns.length);
  auditSheet.getCell('A1').font = { bold: true, size: 14 };
  auditSheet.getCell('A1').alignment = { horizontal: 'center' };
  auditSheet.insertRow(2, [`Filtros: ${buildFiltersText(filters)}`]);
  auditSheet.mergeCells(2, 1, 2, columns.length);
  auditSheet.getCell('A2').alignment = { wrapText: true };
  auditSheet.addRow([]);
  const headerRow = auditSheet.addRow(columns.map((column) => column.header));
  styleHeader(headerRow);

  actions.forEach((action, index) => {
    const summary = mapActionSummary(action);
    const detail = mapActionDetailLines(action, resolveReference);
    const changesChunks = chunkExcelText(detail.changesText);
    const additionalChunks = chunkExcelText(detail.additionalText);
    const chunkCount = Math.max(changesChunks.length, additionalChunks.length);

    for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex += 1) {
      const isFirstChunk = chunkIndex === 0;
      const row = auditSheet.addRow([
        isFirstChunk ? toExcelSafeValue(summary.id) : '',
        isFirstChunk ? toExcelSafeValue(summary.fecha) : '',
        isFirstChunk ? toExcelSafeValue(summary.usuario) : '',
        isFirstChunk ? toExcelSafeValue(summary.modulo) : '',
        isFirstChunk ? toExcelSafeValue(summary.accion) : '',
        isFirstChunk ? toExcelSafeValue(summary.metodo) : '',
        isFirstChunk ? toExcelSafeValue(summary.endpoint) : '',
        isFirstChunk ? toExcelSafeValue(summary.estado) : '',
        isFirstChunk ? toExcelSafeValue(summary.ip) : '',
        isFirstChunk ? toExcelSafeValue(detail.entity) : '',
        isFirstChunk ? toExcelSafeValue(detail.changesCount) : '',
        changesChunks[chunkIndex] || '',
        additionalChunks[chunkIndex] || '',
      ]);
      styleDataRow(row, index);
    }
  });

  const buffer = await workbook.xlsx.writeBuffer({
    useSharedStrings: false,
    useStyles: true,
  });
  const filename = `${filenamePrefix}_${buildTimestamp()}.xlsx`;
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
};

export const exportAuditActionsToPdf = ({
  actions = [],
  filters = {},
  resolveReference,
  filenamePrefix = 'Auditoria',
}) => {
  const doc = new jsPDF('portrait');
  writePdfTitle(doc, 'Auditoría de acciones');
  const filtersText = buildFiltersText(filters);
  let y = writePdfMeta(doc, { filtersText, total: actions.length });

  actions.forEach((action, index) => {
    y = ensurePdfSpace(doc, y, 46);

    const summary = mapActionSummary(action);
    const detail = mapActionDetailLines(action, resolveReference);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(36, 36, 36);
    doc.text(`#${index + 1} | Acción ${summary.id}`, 14, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const summaryLine =
      `${summary.fecha} | ${summary.usuario} | ${summary.modulo} | ${summary.metodo} ${summary.endpoint} | Estado ${summary.estado}`;
    const summaryLines = doc.splitTextToSize(summaryLine, 182);
    doc.text(summaryLines, 14, y);
    y += summaryLines.length * 4 + 1;

    const actionLines = doc.splitTextToSize(`Descripción: ${summary.accion}`, 182);
    doc.text(actionLines, 14, y);
    y += actionLines.length * 4 + 1;

    const entityLines = doc.splitTextToSize(`Registro: ${detail.entity}`, 182);
    doc.text(entityLines, 14, y);
    y += entityLines.length * 4 + 1;

    const changesLines = doc.splitTextToSize(
      `Cambios (${detail.changesCount}): ${detail.changesText}`,
      182
    );
    doc.text(changesLines, 14, y);
    y += changesLines.length * 4 + 1;

    const additionalLines = doc.splitTextToSize(
      `Detalles adicionales: ${detail.additionalText}`,
      182
    );
    doc.text(additionalLines, 14, y);
    y += additionalLines.length * 4 + 4;

    doc.setDrawColor(225, 225, 225);
    doc.line(14, y, 196, y);
    y += 4;
  });

  doc.save(`${filenamePrefix}_${buildTimestamp()}.pdf`);
};
